import type { ParamsProps } from '@/components/WdiBus/FormPage';
import type { WdiModalAppResult } from '@/components/WdiControls';
import { SoldToGroup } from '@/services/mdm/soldtogroup';
import { CustSys } from '@/services/mdm/custsys';
import { Staff } from '@/services/basic/staff';
import { SalesContract } from '@/services/contract/salescontract';
import { SalesContractHistory } from '@/services/contract/salescontracthistory';
import type { StatusEnum } from '@/enums/status';
import { SoldTo } from '@/services/mdm/soldto';
import { ResponseCode } from '@/utils/response';
import { ResponseStatus } from '@/utils/request';
import moment from 'moment';
import { FileUploadUtility } from '@/components/WdiControls/WdiFileUpload';
import { Enum } from '@/enums/system';
import { BOHQ } from '@/services/mdm/bohq';
import { WdiSelectFormat } from '@/components/WdiControls/WdiSelect';

export type ViewEntityProps = {
  statusId?: StatusEnum.FormStatus;
  contractType?: number;
  salesContractId?: number;
  salesContract?: API.SalesContract;
  soldToGroup?: API.SoldToGroup;
  soldTo?: API.SoldTo;
  custSys?: API.CustSys;
  modifiedUser?: API.Staff;
  bohq?: API.BOHQ;
  formTypeId?: number;
  pageTypeId?: number;
  pageGroupId?: number;
};

export type ViewEntityHistoryProps = {
  salesContractHistoryId?: number;
  contractType?: number;
  salesContractHistory?: API.SalesContractHistory;
  soldToGroup?: API.SoldToGroup;
  soldTo?: API.SoldTo;
  custSys?: API.CustSys;
  modifiedUser?: API.Staff;
  bohq?: API.BOHQ;
  formTypeId?: number;
  pageTypeId?: number;
  pageGroupId?: number;
};

export const isContractType1 = (pageTypeId?: number) => {
  return !!(pageTypeId && pageTypeId == Enum.PageType.CONTRACT_APPLICATION);
};

export const isContractType2 = (pageTypeId?: number) => {
  return false;
  // return !!(pageTypeId && pageTypeId == Enum.SalesContractType.SELLOUT);
};

export const initPageContext = async (params: ParamsProps) => {
  const result: WdiModalAppResult = {
    code: ResponseCode.UNKNOWN,
  };
  const viewEntity: ViewEntityProps = {};
  if (params.contractType) {
    viewEntity.contractType = params.contractType;
  }
  if (params.formTypeId) {
    viewEntity.formTypeId = params.formTypeId;
    viewEntity.pageTypeId = params.pageTypeId;
    viewEntity.pageGroupId = params.pageGroupId;
  }
  if (params.salesContractId) {
    viewEntity.salesContractId = params.salesContractId;
    viewEntity.salesContract = (await SalesContract.get(params.salesContractId)).data;
    if (viewEntity.salesContract) {
      viewEntity.formTypeId = viewEntity.salesContract.formTypeId;
      viewEntity.pageTypeId = viewEntity.salesContract.pageTypeId;
      viewEntity.pageGroupId = viewEntity.salesContract.pageGroupId;
      if (viewEntity.salesContract.soldToGroupId) {
        viewEntity.soldToGroup = (await SoldToGroup.get(viewEntity.salesContract.soldToGroupId)).data;
      }
      if (viewEntity.salesContract.custSysCode) {
        viewEntity.custSys = (await CustSys.get(String(viewEntity.salesContract.custSysCode))).data;
      }
      if (viewEntity.salesContract.soldToCode) {
        viewEntity.soldTo = (await SoldTo.get(viewEntity.salesContract.soldToCode)).data;
      }
      if (viewEntity.salesContract.bohqId) {
        viewEntity.bohq = (await BOHQ.get(viewEntity.salesContract.bohqId)).data;
      }
      viewEntity.modifiedUser = (
        await Staff.get(Number(viewEntity.salesContract.modifiedUserId))
      ).data;
    }
  }

  result.code = ResponseCode.SUCCESS;
  result.data = viewEntity;
  return result;
};

export const initHistoryPageContext = async (id: number) => {
  const result: WdiModalAppResult = {
    code: ResponseCode.UNKNOWN,
  };

  const viewEntity: ViewEntityHistoryProps = {};
  if (id) {
    viewEntity.salesContractHistoryId = id;
    viewEntity.salesContractHistory = (await SalesContractHistory.get(id)).data;
    if (viewEntity.salesContractHistory) {
      viewEntity.contractType = viewEntity.salesContractHistory.contractType;
      if (viewEntity.salesContractHistory.soldToGroupId) {
        viewEntity.soldToGroup = (
          await SoldToGroup.get(viewEntity.salesContractHistory.soldToGroupId)
        ).data;
      }
      if (viewEntity.salesContractHistory.custSysCode) {
        viewEntity.custSys = (
          await CustSys.get(String(viewEntity.salesContractHistory.custSysCode))
        ).data;
      }
      if (viewEntity.salesContractHistory.soldToCode) {
        viewEntity.soldTo = (await SoldTo.get(viewEntity.salesContractHistory.soldToCode)).data;
      }
      if (viewEntity.salesContractHistory.bohqId) {
        viewEntity.bohq = (await BOHQ.get(viewEntity.salesContractHistory.bohqId)).data;
      }
      viewEntity.modifiedUser = (
        await Staff.get(Number(viewEntity.salesContractHistory.modifiedUserId))
      ).data;
    }
  }

  result.code = ResponseCode.SUCCESS;
  result.data = viewEntity;
  return result;
};

export const getPageResult = (values: any, viewEntity?: ViewEntityProps, dataSource?: any[]) => {
  const result: WdiModalAppResult = {
    code: ResponseStatus.UNKNOWN,
  };

  const { contractDate, signDate, fileInfo, isAutoAppend, custSysCode, boIds, bohqId, ...entity } =
    values;

  if (contractDate && contractDate.length == 2) {
    entity.beginDate = moment(contractDate[0]).format('YYYY-MM-DD');
    entity.endDate = moment(contractDate[1]).format('YYYY-MM-DD');
  } else {
    result.message = '无效的合同期间';
    return result;
  }
  if (boIds) {
    entity.boIds = boIds.map((item: any) => item.value).join();
  }
  if (bohqId) {
    entity.bohqId = WdiSelectFormat.getvalue(bohqId);
  }

  if (signDate) {
    entity.signDate = moment(signDate).format('YYYY-MM-DD');
  }

  if (FileUploadUtility.isUploading(fileInfo)) {
    result.message = '附件正在上传中!';
    return result;
  }
  if (FileUploadUtility.isError(fileInfo)) {
    result.message = '附件存在错误文件!';
    return result;
  }
  const info = FileUploadUtility.serialize(fileInfo);
  entity.fileName = info?.names;
  entity.filePath = info?.urls;

  entity.isAutoAppend = Boolean(isAutoAppend);
  entity.custSysCode = custSysCode;

  const data: API.SalesContractPostParam = {
    salesContract: {},
    salesContractTermsList: [],
  };

  if (viewEntity && viewEntity.formTypeId) {
    entity.formTypeId = viewEntity?.formTypeId;
    entity.pageTypeId = viewEntity?.pageTypeId;
    entity.pageGroupId = viewEntity?.pageGroupId;
  }

  if (viewEntity && viewEntity.salesContract) {
    const salesContract = viewEntity.salesContract;
    entity.salesContractId = salesContract.salesContractId;
    entity.contractVersion = salesContract.contractVersion;
  } else {
    entity.contractVersion = 1;
  }

  data.salesContract = entity;
  if (dataSource && dataSource.length) {
    dataSource.forEach((item, itemIndex) => {
      data.salesContractTermsList.push({
        ...item,
        ...{
          rowNumber: itemIndex + 1,
        },
      });
    });
  } else {
    result.message = '合同明细至少填写一条!';
    return result;
  }
  result.data = data;
  result.code = ResponseStatus.SUCCESS;
  return result;
};
